import React, { useState, useEffect, useRef, memo } from 'react';
import { Trash2, Edit, Save, XCircle, Eye, EyeOff, Barcode, ChevronLeft, ChevronRight, ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';
import { EmptyState } from './uiComponents';
import { IconButton, Button } from '../../components/ui';

// Column definitions for the gift-card table header. `right` flags numeric
// columns that should be right-aligned to match the tabular cell values.
// `sortKey`/`sortType` mark a column as click-to-sort (handled by the
// SortableHeader below); columns without a sortKey render as plain labels.
const TABLE_COLUMNS = [
    { label: 'Retailer', sortKey: 'retailer', sortType: 'text' },
    { label: 'Value', right: true, sortKey: 'value', sortType: 'number' },
    { label: 'Price Paid', right: true, sortKey: 'pricePaid', sortType: 'number' },
    { label: '% Off', right: true, sortKey: 'percentOff', sortType: 'number' },
    { label: 'Spent', right: true, sortKey: 'spent', sortType: 'number' },
    { label: 'Remaining', right: true, sortKey: 'remaining', sortType: 'number' },
    { label: 'Code' },
    { label: 'PIN' },
    { label: 'Source', sortKey: 'source', sortType: 'text' },
    { label: 'Notes', sortKey: 'notes', sortType: 'text' },
    { label: 'Actions' },
];

// Clickable column header. Non-sortable columns render as a plain <th>.
// Sortable columns show the active direction (▲/▼) and, when more than one
// sort is active, a small priority number so the precedence is visible.
const SortableHeader = ({ col, sortConfig, onSort }) => {
    const baseClass = `px-2 py-2 ${col.right ? 'text-right' : ''}`;

    if (!col.sortKey) {
        return <th scope="col" className={baseClass}>{col.label}</th>;
    }

    const sortIndex = sortConfig.findIndex(s => s.key === col.sortKey);
    const isActive = sortIndex !== -1;
    const direction = isActive ? sortConfig[sortIndex].direction : null;
    const showPriority = isActive && sortConfig.length > 1;

    return (
        <th scope="col" className={baseClass} aria-sort={isActive ? (direction === 'asc' ? 'ascending' : 'descending') : 'none'}>
            <button
                type="button"
                onClick={() => onSort(col.sortKey, col.sortType)}
                title={`Sort by ${col.label}`}
                className={`group inline-flex items-center gap-1 uppercase rounded px-1 -mx-1 transition-colors hover:text-green-700 dark:hover:text-green-400 focus:outline-none focus:ring-2 focus:ring-green-500 ${isActive ? 'text-green-700 dark:text-green-400 font-bold' : ''}`}
            >
                <span>{col.label}</span>
                {isActive
                    ? (direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)
                    : <ChevronsUpDown size={14} className="opacity-30 group-hover:opacity-60 transition-opacity" />}
                {showPriority && <span className="text-[10px] font-bold tabular-nums leading-none">{sortIndex + 1}</span>}
            </button>
        </th>
    );
};

// --- Reusable Form Input ---
export const FormInput = ({ label, name, ...props }) => (
    <div>
        <label htmlFor={name} className="block mb-1 text-sm font-medium text-gray-900 dark:text-white">{label}</label>
        <input id={name} name={name} {...props} className="bg-gray-50 dark:bg-slate-700 border border-gray-300 dark:border-slate-600 text-gray-900 dark:text-white text-sm rounded-lg focus:ring-2 focus:ring-green-800 dark:focus:ring-green-500 block w-full p-2.5" />
    </div>
);

// --- Autocomplete Input Component ---
const AutocompleteFormInput = ({ label, name, value, onChange, items, ...props }) => {
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);

    const handleChange = (e) => {
        const { value: inputValue } = e.target;
        onChange(e);

        if (inputValue.length > 0) {
            const filteredSuggestions = items.filter(item =>
                item.toLowerCase().includes(inputValue.toLowerCase())
            ).slice(0, 5);
            setSuggestions(filteredSuggestions);
            setShowSuggestions(true);
        } else {
            setShowSuggestions(false);
        }
    };

    const handleSuggestionClick = (suggestion) => {
        const event = { target: { name, value: suggestion } };
        onChange(event);
        setShowSuggestions(false);
    };

    return (
        <div className="relative">
            <FormInput
                label={label}
                name={name}
                value={value}
                onChange={handleChange}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                autoComplete="off"
                {...props}
            />
            {showSuggestions && suggestions.length > 0 && (
                <ul className="absolute z-20 w-full bg-white dark:bg-slate-700 border border-gray-300 dark:border-slate-600 rounded-md mt-1 max-h-60 overflow-y-auto shadow-lg">
                    {suggestions.map((suggestion, index) => (
                        <li
                            key={index}
                            className="px-4 py-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-slate-600 text-gray-900 dark:text-white"
                            onMouseDown={() => handleSuggestionClick(suggestion)}
                        >
                            {suggestion}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

// --- Barcode Component ---
const BarcodeDisplay = ({ value }) => {
    const svgRef = useRef(null);

    useEffect(() => {
        const scriptId = 'jsbarcode-script';
        const renderBarcode = () => {
            if (svgRef.current && window.JsBarcode) {
                try {
                    window.JsBarcode(svgRef.current, value, {
                        format: "CODE128",
                        lineColor: "#000",
                        width: 2,
                        height: 80,
                        displayValue: true,
                        fontOptions: "bold",
                        fontSize: 16
                    });
                } catch (e) {
                    console.error("JsBarcode error:", e);
                    svgRef.current.innerHTML = `<text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="red">Invalid Barcode</text>`;
                }
            }
        };

        if (!document.getElementById(scriptId)) {
            const script = document.createElement('script');
            script.id = scriptId;
            script.src = "https://cdn.jsdelivr.net/npm/jsbarcode@3.11.5/dist/JsBarcode.all.min.js";
            script.async = true;
            document.body.appendChild(script);
            script.onload = renderBarcode;
        } else {
            renderBarcode();
        }
    }, [value]);

    return <svg ref={svgRef} className="mx-auto"></svg>;
};

// --- Pagination Controls Component ---
const PaginationControls = ({ currentPage, totalPages, onPageChange, totalItems, itemsPerPage }) => {
    if (totalPages <= 1) return null;

    const startItem = (currentPage - 1) * itemsPerPage + 1;
    const endItem = Math.min(currentPage * itemsPerPage, totalItems);

    return (
        <div className="flex items-center justify-between mt-4 text-sm text-gray-700 dark:text-gray-300">
            <div>
                Showing <span className="font-semibold">{startItem}</span> to <span className="font-semibold">{endItem}</span> of <span className="font-semibold">{totalItems}</span> results
            </div>
            <div className="flex items-center space-x-2">
                <button
                    onClick={() => onPageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="flex items-center justify-center p-2 border border-gray-300 dark:border-slate-600 rounded-md hover:bg-gray-50 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    aria-label="Previous Page"
                >
                    <ChevronLeft size={16} />
                </button>
                <span className="px-2 font-medium">
                    Page {currentPage} of {totalPages}
                </span>
                <button
                    onClick={() => onPageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="flex items-center justify-center p-2 border border-gray-300 dark:border-slate-600 rounded-md hover:bg-gray-50 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    aria-label="Next Page"
                >
                    <ChevronRight size={16} />
                </button>
            </div>
        </div>
    );
};


// --- Gift Card Table & Rows ---
// *** FIX 2: Add onAddNew to the list of props the component accepts ***
export const GiftCardTable = ({ cards, sortConfig = [], onSort, editingId, onEdit, onSave, onDelete, isLocked, revealedCardIds, onReveal, retailerNames, onGenerateBarcode, barcodeCardIds, onAddNew, selectedCardIds, onCardSelect, onSelectAll }) => {
    const [currentPage, setCurrentPage] = useState(1);
    const ITEMS_PER_PAGE = 15;

    // Re-sorting reshuffles the whole list, so jump back to the first page.
    useEffect(() => {
        setCurrentPage(1);
    }, [sortConfig]);

    // Only reset page when the total number of cards changes significantly
    const prevCardsLength = useRef(cards.length);
    useEffect(() => {
        // Reset to page 1 only when cards are added/removed, not on re-renders
        if (Math.abs(cards.length - prevCardsLength.current) > 0) {
            // If we deleted cards and are now past the last page, go to the last available page
            const newTotalPages = Math.ceil(cards.length / ITEMS_PER_PAGE);
            if (currentPage > newTotalPages && newTotalPages > 0) {
                setCurrentPage(newTotalPages);
            } else if (cards.length > prevCardsLength.current) {
                // New cards added, reset to page 1
                setCurrentPage(1);
            }
            prevCardsLength.current = cards.length;
        }
    }, [cards.length, currentPage]);

    const totalPages = Math.ceil(cards.length / ITEMS_PER_PAGE);
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const paginatedCards = cards.slice(startIndex, startIndex + ITEMS_PER_PAGE);

    const handlePageChange = (page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    if (cards.length === 0 && !isLocked) {
        // This line is now fixed because of the changes above
        return <EmptyState onAddClick={onAddNew} />;
    }

    return (
        <React.Fragment>
            <div className="overflow-x-auto">
                <table className="w-full text-sm text-left text-gray-600 dark:text-gray-300">
                    <thead className="text-xs text-gray-700 dark:text-gray-200 uppercase bg-gray-100 dark:bg-slate-700 rounded-t-lg">
                        <tr>
                            {/* ADD this table header for the checkbox */}
                            <th scope="col" className="p-3">
                                <input type="checkbox" onChange={onSelectAll} aria-label="Select all cards on this page"
                                    checked={cards.length > 0 && selectedCardIds.size === cards.length}
                                    className="w-4 h-4 text-green-600 bg-gray-100 dark:bg-slate-600 border-gray-300 dark:border-slate-500 rounded focus:ring-green-500" />
                            </th>
                            {TABLE_COLUMNS.map(col => <SortableHeader key={col.label} col={col} sortConfig={sortConfig} onSort={onSort} />)}
                        </tr>
                    </thead>
                    <tbody>
                        {paginatedCards.map((card, index) => (
                            editingId === card.id && !isLocked
                                ? <CardForm key={card.id} initialData={card} onSave={(data) => onSave(card.id, data)} onCancel={() => onEdit(null)} isEditing={true} retailerNames={retailerNames} />
                                : <CardRow key={card.id} index={index} card={card} onEdit={onEdit} onDelete={onDelete} isLocked={isLocked} onSave={onSave} revealedCardIds={revealedCardIds} onReveal={onReveal} onGenerateBarcode={onGenerateBarcode} barcodeCardIds={barcodeCardIds}
                                    isSelected={selectedCardIds.has(card.id)}
                                    onCardSelect={onCardSelect} />
                        ))}
                    </tbody>
                </table>
            </div>
            <PaginationControls
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                totalItems={cards.length}
                itemsPerPage={ITEMS_PER_PAGE}
            />
        </React.Fragment>
    );
};

// ... (The rest of the file remains unchanged)
// Memoized CardRow to prevent re-renders when card data hasn't changed
export const CardRow = memo(({ card, index = 0, onEdit, onDelete, isLocked, onSave, revealedCardIds, onReveal, onGenerateBarcode, barcodeCardIds, isSelected, onCardSelect }) => {
    const [isEditingSpent, setIsEditingSpent] = useState(false);
    const [spentValue, setSpentValue] = useState(card.spent.toFixed(2));
    const remaining = card.initialValue - card.spent;
    const percentOff = card.initialValue > 0 ? ((card.initialValue - card.pricePaid) / card.initialValue) * 100 : 0;
    const isRevealed = revealedCardIds.includes(card.id);
    const lockedClass = isLocked ? 'blur-sm select-none' : '';
    const maskCardNumber = (num) => { if (!num || num.length <= 4) return '••••'; return `•••• ${num.slice(-4)}`; };
    const handleSpentUpdate = () => { const newSpent = parseFloat(spentValue); if (!isNaN(newSpent) && newSpent !== card.spent) { onSave(card.id, { ...card, spent: newSpent }); } setIsEditingSpent(false); };
    const handleKeyDown = (e) => { if (e.key === 'Enter') { handleSpentUpdate(); } else if (e.key === 'Escape') { setSpentValue(card.spent.toFixed(2)); setIsEditingSpent(false); } };

    // FIX: Updated logic to check if the card's ID is in the array of visible barcodes
    const showBarcode = barcodeCardIds.includes(card.id) && isRevealed;
    const barcodeRowRef = useRef(null);

    useEffect(() => {
        if (showBarcode && barcodeRowRef.current) {
            barcodeRowRef.current.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
                inline: 'center'
            });
        }
    }, [showBarcode]);

    return (
        <React.Fragment>
            <tr className={`${index % 2 === 1 ? 'bg-gray-50 dark:bg-slate-800/40' : 'bg-white dark:bg-slate-800'} border-b border-gray-100 dark:border-slate-700 hover:bg-brand-50/60 dark:hover:bg-slate-700/70 transition-colors`}>
                <td className="w-4 p-2"><input type="checkbox" checked={isSelected} onChange={() => onCardSelect(card.id)} aria-label={`Select ${card.retailer} gift card`} className="w-4 h-4 text-green-600 bg-gray-100 dark:bg-slate-600 border-gray-300 dark:border-slate-500 rounded focus:ring-green-500" /></td>
                <td className="px-2 py-2 font-medium text-gray-900 dark:text-white">{card.retailer}</td>
                <td className="px-2 py-2 whitespace-nowrap text-right tabular-nums">${card.initialValue.toFixed(2)}</td>
                <td className="px-2 py-2 whitespace-nowrap text-right tabular-nums">${card.pricePaid.toFixed(2)}</td>
                <td className="px-2 py-2 whitespace-nowrap text-right tabular-nums">{percentOff.toFixed(2)}%</td>
                <td className="px-2 py-2 whitespace-nowrap text-right tabular-nums text-red-600 dark:text-red-400 font-semibold cursor-pointer" onClick={() => !isLocked && setIsEditingSpent(true)}> {isEditingSpent ? (<input type="number" value={spentValue} onChange={(e) => setSpentValue(e.target.value)} onBlur={handleSpentUpdate} onKeyDown={handleKeyDown} className="w-20 bg-gray-100 dark:bg-slate-700 border border-green-300 dark:border-green-600 rounded p-1 focus:ring-2 focus:ring-green-800 dark:text-white text-sm text-right tabular-nums" autoFocus />) : (`$${card.spent.toFixed(2)}`)} </td>
                <td className="px-2 py-2 whitespace-nowrap text-right tabular-nums text-green-700 dark:text-green-400 font-bold">${remaining.toFixed(2)}</td>
                <td className={`px-2 py-2 font-mono text-xs whitespace-nowrap ${lockedClass}`}> {isLocked ? '************' : (isRevealed ? card.code : maskCardNumber(card.code))} </td>
                <td className={`px-2 py-2 font-mono whitespace-nowrap ${lockedClass}`}> {isLocked ? '****' : (isRevealed ? card.pin : '****')} </td>
                <td className="px-2 py-2">{card.source}</td>
                <td className="px-2 py-2 max-w-[200px] whitespace-normal break-words" title={card.notes}>{card.notes}</td>
                <td className="px-2 py-2 whitespace-nowrap">
                    <div className="flex items-center space-x-1">
                        <IconButton icon={Barcode} size={16} label="Generate Barcode" onClick={() => onGenerateBarcode(card)} disabled={isLocked || !isRevealed} />
                        <IconButton icon={isRevealed ? EyeOff : Eye} size={16} label={isRevealed ? "Hide Details" : "Reveal Details"} onClick={() => onReveal(card.id)} disabled={isLocked} />
                        <IconButton icon={Edit} size={16} variant="brand" label="Edit Card" onClick={() => onEdit(card.id)} disabled={isLocked} />
                        <IconButton icon={Trash2} size={16} variant="danger" label="Delete Card" onClick={() => onDelete(card.id)} disabled={isLocked} />
                    </div>
                </td>
            </tr>
            {showBarcode && (
                <tr ref={barcodeRowRef} className="bg-gray-50 dark:bg-slate-700 border-b dark:border-slate-600">
                    <td colSpan="12" className="p-4 text-center">
                        <BarcodeDisplay value={card.code} />
                    </td>
                </tr>
            )}
        </React.Fragment>
    );
});

export const CardForm = ({ initialData, onSave, onCancel, isEditing = false, retailerNames = [] }) => {
    const [formData, setFormData] = useState(initialData || { retailer: '', initialValue: '', pricePaid: '', spent: 0, code: '', pin: '', source: '', notes: '' });
    const handleChange = (e) => { const { name, value } = e.target; setFormData(prev => ({ ...prev, [name]: value })); };
    const handleSubmit = (e) => { e.preventDefault(); onSave(formData); };
    return (
        <tr className={isEditing ? 'bg-brand-50 dark:bg-brand-900/20' : ''}>
            {/* The colSpan needs to be 12 to account for the new checkbox column */}
            <td colSpan="12" className="p-4">
                <form onSubmit={handleSubmit}>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4 mb-4">
                        <div className="lg:col-span-2">
                            <AutocompleteFormInput name="retailer" label="Retailer" value={formData.retailer} onChange={handleChange} items={retailerNames} placeholder="e.g., Target" required />
                        </div>
                        <div>
                            <FormInput name="initialValue" label="Value ($)" value={formData.initialValue} onChange={handleChange} type="number" step="0.01" placeholder="50.00" required />
                        </div>
                        <div>
                            <FormInput name="pricePaid" label="Paid ($)" value={formData.pricePaid} onChange={handleChange} type="number" step="0.01" placeholder="45.00" required />
                        </div>
                        <div>
                            <FormInput name="spent" label="Spent ($)" value={formData.spent} onChange={handleChange} type="number" step="0.01" placeholder="0.00" />
                        </div>
                        <div className="lg:col-span-2">
                            <FormInput name="code" label="Code" value={formData.code} onChange={handleChange} placeholder="Card number" />
                        </div>
                        <div>
                            <FormInput name="pin" label="PIN" value={formData.pin} onChange={handleChange} placeholder="1234" />
                        </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4 items-end">
                        <div className="lg:col-span-2">
                            <FormInput name="source" label="Source" value={formData.source} onChange={handleChange} placeholder="e.g., Card Depot" />
                        </div>
                        <div className="lg:col-span-4">
                            <FormInput name="notes" label="Notes" value={formData.notes} onChange={handleChange} placeholder="Optional notes" />
                        </div>
                        <div className="lg:col-span-2 flex items-center justify-end space-x-3">
                            <Button type="button" variant="ghost" icon={XCircle} onClick={onCancel}>Cancel</Button>
                            <Button type="submit" icon={Save}>Save Card</Button>
                        </div>
                    </div>
                </form>
            </td>
        </tr>
    );
};

// --- Store Summary Table ---
export const StoreSummaryTable = ({ storeSummary }) => {
    const stores = Object.keys(storeSummary);
    if (stores.length === 0) return null;
    return (
        <div className="mt-8 bg-white dark:bg-slate-800 p-4 rounded-xl shadow-lg">
            <h2 className="text-lg font-bold text-gray-700 dark:text-white mb-4">Store Summary</h2>
            <div className="overflow-x-auto">
                <table className="w-full text-sm text-left text-gray-600 dark:text-gray-300">
                    <thead className="text-xs text-gray-700 dark:text-gray-200 uppercase bg-gray-100 dark:bg-slate-700">
                        <tr>
                            <th scope="col" className="px-2 py-2 whitespace-nowrap">Store</th>
                            <th scope="col" className="px-2 py-2 text-right whitespace-nowrap">Total Value</th>
                            <th scope="col" className="px-2 py-2 text-right whitespace-nowrap">Total Paid</th>
                            <th scope="col" className="px-2 py-2 text-right whitespace-nowrap">Total Spent</th>
                            <th scope="col" className="px-2 py-2 text-right whitespace-nowrap">Total Remaining</th>
                        </tr>
                    </thead>
                    <tbody>
                        {Object.keys(storeSummary).sort().map(store => (
                            <tr key={store} className="bg-white dark:bg-slate-800 border-b dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-700">
                                <td className="px-2 py-2 font-medium text-gray-900 dark:text-white whitespace-nowrap">{store}</td>
                                <td className="px-2 py-2 text-right whitespace-nowrap">${storeSummary[store].value.toFixed(2)}</td>
                                <td className="px-2 py-2 text-right whitespace-nowrap">${storeSummary[store].paid.toFixed(2)}</td>
                                <td className="px-2 py-2 text-right text-red-600 dark:text-red-400 whitespace-nowrap">${storeSummary[store].spent.toFixed(2)}</td>
                                <td className="px-2 py-2 text-right text-green-700 dark:text-green-400 font-bold whitespace-nowrap">${storeSummary[store].remaining.toFixed(2)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};